import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'

import { Editor } from '@/common/components/Editor'
import UploadImage from '@/common/components/UploadImage'
import useMutationGetAnnouncementBannerUploadFileUrl from '@/modules/client-setting/api/hooks/useMutationGetAnnouncementBannerUploadFileUrl'

const formSchema = z.object({
	title: z.string().min(1, 'กรุณากรอกหัวข้อ'),
	type: z.literal('content'),
	content: z.string().optional(),
	image: z.string().optional(),
	link: z.string().optional(),
	linkText: z.string().optional(),
	targetPaths: z.string().optional(),
	order: z
		.number()
		.min(0, 'ลำดับต้องมากกว่าหรือเท่ากับ 0')
		.optional()
		.nullable(),
})

export type LoginAnnounceFormValues = z.infer<typeof formSchema>

export const normalizeTargetPaths = (targetPaths?: string | null) =>
	targetPaths
		?.split(/\r?\n/)
		.map((path) => path.trim())
		.filter(Boolean) ?? []

interface LoginAnnounceFormProps {
	defaultValues?: LoginAnnounceFormValues
	onSubmit: (data: LoginAnnounceFormValues) => void
	submitButtonText: string
	showOrder?: boolean
}

export default function LoginAnnounceForm({
	defaultValues = {
		title: '',
		type: 'content',
		content: '',
		image: '',
		order: 0,
		link: '',
		linkText: '',
		targetPaths: '',
	},
	onSubmit,
	submitButtonText,
	showOrder = false,
}: LoginAnnounceFormProps) {
	const form = useForm<LoginAnnounceFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues,
	})

	const { mutateAsync: getAnnouncementBannerFileUrl } =
		useMutationGetAnnouncementBannerUploadFileUrl()

	const watchType = form.watch('type')

	const handleImageUploaded = (fileUrl: string) => {
		form.setValue('image', fileUrl)
	}

	return (
		<div className="max-h-[80vh] overflow-y-auto pr-2">
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-4">
					<FormField
						control={form.control}
						name="title"
						render={({ field }) => (
							<FormItem>
								<FormLabel>หัวข้อ</FormLabel>
								<FormControl>
									<Input {...field} placeholder="กรอกหัวข้อ" />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="type"
						render={({ field }) => (
							<FormItem>
								<FormLabel>ประเภท</FormLabel>
								<Select
									onValueChange={field.onChange}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder="เลือกประเภท" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										<SelectItem value="content">เนื้อหา</SelectItem>
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					{watchType === 'content' && (
						<FormField
							control={form.control}
							name="content"
							render={({ field }) => (
								<FormItem>
									<FormLabel>เนื้อหา</FormLabel>
									<FormControl>
										<Editor
											value={field.value || ''}
											onChange={field.onChange}
											placeholder="กรอกคำอธิบาย"
											className="min-h-[200px] [&_.ql-container]:!min-h-[150px]"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					<FormField
						control={form.control}
						name="image"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									รูปภาพแบนเนอร์{' '}
									<span className="text-amber-600 dark:text-amber-400">
										(สัดส่วน 3:2)
									</span>
								</FormLabel>
								<FormControl>
									<UploadImage
										onImageUploaded={handleImageUploaded}
										defaultValue={field.value || ''}
										getFileUrl={() =>
											getAnnouncementBannerFileUrl({
												payload: { fileExtension: '' },
											})
										}
										aspectRatio="3/2"
										previewHeight={200}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="link"
						render={({ field }) => (
							<FormItem>
								<FormLabel>ลิงก์</FormLabel>
								<FormControl>
									<Input {...field} placeholder="https://example.com" />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="linkText"
						render={({ field }) => (
							<FormItem>
								<FormLabel>ข้อความลิงก์ (ตัวอย่าง: อ่านเพิ่มเติม)</FormLabel>
								<FormControl>
									<Input {...field} placeholder="อ่านเพิ่มเติม" />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="targetPaths"
						render={({ field }) => (
							<FormItem>
								<FormLabel>แสดงเฉพาะหน้า</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										placeholder={`เช่น\n/profile\n/promotions\n/daily-checkin`}
										className="min-h-24"
									/>
								</FormControl>
								<FormDescription>
									ใส่หนึ่ง path ต่อหนึ่งบรรทัด เช่น /promotions
									เว้นว่างไว้เพื่อแสดงทันทีหลังผู้เล่นเข้าสู่ระบบ
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					{showOrder && (
						<FormField
							control={form.control}
							name="order"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ลำดับ</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											{...field}
											value={field.value ?? ''}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					<Button type="submit" variant="default" className="w-full">
						{submitButtonText}
					</Button>
				</form>
			</Form>
		</div>
	)
}
